/**
 * Irreversible-purge modal — TASK 10.4 Step 3 (AC 2.9), the mock's `ddm`
 * (L813-826 / `_ddm()` L1149) EVOLVED from the static shell extraction in
 * `app/overlays.js#DeleteDataModal`:
 *
 *   - the TYPED CONFIRMATION VALUE is handed to `onSubmit` — the server's
 *     `POST /api/admin/users/:id/delete-data` re-validates `{ confirm }`
 *     itself (`deleteDataRequestSchema`), so the client must forward what
 *     was actually typed rather than a boolean "it matched here";
 *   - {@link deleteDataConfirmMatches} is exported pure, so the gating rule
 *     (exact email, or the literal word DELETE) is unit-pinnable;
 *   - {@link openDeleteDataModal} owns the open/close-then-act choreography
 *     on the OverlayStack, which provides the focus trap, Escape/backdrop
 *     dismissal and history sentinel (AC 2.9 "focus-trapped via OverlayStack").
 *
 * Purge stays DISABLED until the typed value matches (mock `match`, L1149) —
 * a form submit while unmatched is ignored, so Enter cannot bypass the gate.
 *
 * @module
 */

import { append, el } from '../../components/primitives/dom.js';
import { controlTestId } from '../../app/testids.js';

/** mock L1149 — the non-email escape hatch, case-sensitive. */
export const DELETE_DATA_KEYWORD = 'DELETE';

/** mock L816 (title) and L817 (body prefix/suffix) — copy verbatim. */
export const DELETE_DATA_TITLE = 'IRREVERSIBLE PURGE';
export const DELETE_DATA_BODY_PREFIX = 'This wipes all personal data for ';
export const DELETE_DATA_BODY_SUFFIX =
  ' — favorites, playlists, queue, history, downloads, stats. It cannot be undone.';

/**
 * The typed-confirmation gate (mock `match` L1149): the exact target email,
 * or the literal word DELETE. Whitespace around the typed value is ignored;
 * nothing else is normalised — `delete` (lowercase) does NOT purge.
 *
 * @param {string} typed
 * @param {string} email
 * @returns {boolean}
 */
export function deleteDataConfirmMatches(typed, email) {
  const value = typed.trim();
  return value === email || value === DELETE_DATA_KEYWORD;
}

/**
 * Builds the modal DESCRIPTOR (markup only — the OverlayStack owns focus,
 * dismissal and ordering, exactly as every `app/overlays.js` factory).
 *
 * @param {{
 *   email: string,
 *   onSubmit: (confirm: string) => void,
 *   onCancel: () => void,
 * }} options
 *   `onSubmit` receives the raw typed confirmation (trimmed) ONLY after the
 *   client-side match passed; the server re-checks it regardless.
 * @returns {import('../../app/OverlayStack.js').OverlayDescriptor}
 */
export function DeleteDataModal(options) {
  const { email, onSubmit, onCancel } = options;

  const scrim = el('div', {
    className: 'sm-modal-scrim sm-modal-scrim--danger',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Delete user data' },
  });

  const input = el('input', {
    className: 'sm-input sm-input--block',
    attrs: { id: 'sm-ddm', type: 'text', placeholder: email },
  });

  const confirm = el('button', {
    className: 'sm-btn sm-btn--modal-confirm',
    text: 'Purge data',
    testId: controlTestId('Purge data'),
    attrs: { type: 'submit', disabled: true },
    // mock `{{ ddm.btnBg }}` / `{{ ddm.btnOpacity }}`
    styleProps: { background: 'var(--sm-color-text-dim-2)', opacity: '.5' },
  });

  const matches = () =>
    deleteDataConfirmMatches(/** @type {HTMLInputElement} */ (input).value, email);

  input.addEventListener('input', () => {
    const ok = matches();
    if (ok) confirm.removeAttribute('disabled');
    else confirm.setAttribute('disabled', '');
    confirm.style.setProperty(
      'background',
      ok ? 'var(--sm-color-pink)' : 'var(--sm-color-text-dim-2)',
    );
    confirm.style.setProperty('opacity', ok ? '1' : '.5');
  });

  const form = el('form', {
    className: 'sm-modal sm-modal--danger sm-anim-up-fast',
    on: {
      submit: (event) => {
        event.preventDefault();
        if (!matches()) return; // Enter cannot bypass the disabled gate
        onSubmit(/** @type {HTMLInputElement} */ (input).value.trim());
      },
    },
  });

  // mock L817 — the email is emphasised inside the body copy.
  const bodyCopy = el('p', { className: 'sm-body sm-body--modal' });
  append(
    bodyCopy,
    document.createTextNode(DELETE_DATA_BODY_PREFIX),
    el('strong', { className: 'sm-strong', text: email }),
    document.createTextNode(DELETE_DATA_BODY_SUFFIX),
  );

  // mock L818
  const warnLabel = el('label', {
    className: 'sm-form-label sm-form-label--warn',
    attrs: { for: 'sm-ddm' },
  });
  append(
    warnLabel,
    document.createTextNode('Type '),
    el('strong', { text: email }),
    document.createTextNode(` or the word ${DELETE_DATA_KEYWORD} to confirm`),
  );

  append(
    form,
    el('div', {
      className: 'sm-modal__title sm-modal__title--danger',
      text: DELETE_DATA_TITLE,
    }),
    bodyCopy,
    warnLabel,
    input,
    append(
      el('div', { className: 'sm-actions sm-actions--end sm-actions--end-top' }),
      el('button', {
        className: 'sm-btn sm-btn--ghost',
        text: 'Cancel',
        testId: controlTestId('Cancel'),
        attrs: { type: 'button' },
        on: { click: () => onCancel() },
      }),
      confirm,
    ),
  );
  scrim.append(form);

  return { kind: 'deleteData', element: scrim, dialog: scrim, initialFocus: input };
}

/**
 * Opens the modal on the stack and wires the §10.3/§10.5 choreography:
 * close FIRST, then act — `closeTop` consumes the overlay's history sentinel
 * asynchronously, so the purge call runs via `then` once the traversal lands
 * (see OverlayStack's header for why the order matters).
 *
 * @param {{
 *   overlays: import('../../app/OverlayStack.js').OverlayStackHandle,
 *   email: string,
 *   onConfirm: (confirm: string) => void,
 * }} options
 * @returns {import('../../app/OverlayStack.js').OverlayEntry}
 */
export function openDeleteDataModal(options) {
  const { overlays, email, onConfirm } = options;
  return overlays.open(
    DeleteDataModal({
      email,
      onSubmit: (confirm) => {
        overlays.closeTop({ then: () => onConfirm(confirm) });
      },
      onCancel: () => overlays.closeTop(),
    }),
  );
}
